import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { fetchActiveAnnouncements } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useTourStore } from '@/store/tour';
import { useAnnouncementsStore } from '@/store/announcements';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import styles from './AnnouncementCard.module.css';

/**
 * Admin-published landing announcement — a dismissible glass card that
 * floats over the map once it's ready (mounted by MapPage gated on
 * `map &&`). No backdrop scrim, the map stays live underneath.
 *
 * Split dismissal semantics (announcements-in-notifications epic):
 *   - The overlay auto-pops at most ONCE per tab session (autoShown), for the
 *     highest-priority announcement not already closed this session. The rest
 *     live in the notification bell.
 *   - Closing the overlay (X) is a SESSION-ONLY dismiss — it stays in the bell
 *     and returns as an overlay next session. It does NOT hit the server.
 *   - Only the CTA is a PERMANENT dismiss (POST) — acting on the offer retires
 *     it everywhere; the bell's explicit dismiss does the same.
 *   - The bell can re-present any announcement by routing here and setting
 *     `forcedId`; a forced card ignores autoShown + sessionDismissed.
 *
 * Guard chain for the AUTO pop (a forced re-present bypasses these — the
 * driver explicitly asked for it):
 *   1. Tour wins — a pending/active guided tour suppresses the auto pop.
 *   2. Payment onboarding wins — no promos until the driver can charge.
 */
export function AnnouncementCard({
  onNavigate,
}: {
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tourActive = useTourStore((s) => s.active);
  const reducedMotion = useReducedMotion();

  const sessionDismissed = useAnnouncementsStore((s) => s.sessionDismissed);
  const forcedId = useAnnouncementsStore((s) => s.forcedId);
  const autoShown = useAnnouncementsStore((s) => s.autoShown);
  const dismissForSession = useAnnouncementsStore((s) => s.dismissForSession);
  const clearForced = useAnnouncementsStore((s) => s.clearForced);
  const markAutoShown = useAnnouncementsStore((s) => s.markAutoShown);

  const { data: announcements } = useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => fetchActiveAnnouncements().catch(() => []),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const list = announcements ?? [];
  const tourPending = !!user && user.tour_completed_at == null;
  // NO PAYMENT TERM HERE. This used to also require
  // user.has_payment_method !== false, so a driver with no card saw no
  // auto-presented announcement at all — the reasoning being that a new
  // driver who cannot yet charge should not be shown promos first.
  //
  // That reasoning had already been narrowed once, when a card-less
  // super_admin turned out to be silently receiving zero announcements.
  // It broke a second time, and worse, when the v1 mirror delivered
  // drivers who CAN charge on a wallet balance and have no card: the
  // guard read them as mid-onboarding and suppressed everything,
  // permanently, including the launch announcement. An onboarding
  // heuristic is not worth a driver never seeing the one message the
  // whole network has to see.
  //
  // Announcements are already scoped and dismissible on their own terms.
  // If promo-suppression for genuinely new drivers is wanted back, it
  // belongs on the announcement (an audience or a scope), not on a
  // payment field that answers a different question.
  const autoEligible =
    isAuthenticated && !!user && !tourPending && !tourActive;

  // The auto card's target is chosen ONCE and held locally, so flipping the
  // store's autoShown flag (a side effect of presenting) can't retract a card
  // that's already on screen. autoShown only gates whether a NEW auto target
  // may be picked this session.
  const [autoId, setAutoId] = useState<string | null>(null);
  useEffect(() => {
    if (forcedId || autoId || !autoEligible || autoShown) return;
    const cand = list.find((a) => !sessionDismissed.includes(a.id));
    if (cand) {
      setAutoId(cand.id);
      markAutoShown();
    }
  }, [forcedId, autoId, autoEligible, autoShown, list, sessionDismissed, markAutoShown]);

  // Displayed announcement: an explicit re-present (forcedId) wins; otherwise
  // the auto target chosen above. Both resolve against the live list, so a
  // permanent dismissal elsewhere drops it.
  const forced = forcedId ? list.find((a) => a.id === forcedId) ?? null : null;
  const auto = !forced && autoId ? list.find((a) => a.id === autoId) ?? null : null;
  const announcement = forced ?? auto;
  const shownId = announcement?.id ?? null;

  // Reset media-fallback state whenever the shown announcement changes, so a
  // broken image/video on one never suppresses valid media on the next.
  const [imageOk, setImageOk] = useState(true);
  const [videoOk, setVideoOk] = useState(true);
  useEffect(() => {
    setImageOk(true);
    setVideoOk(true);
  }, [shownId]);

  // iOS Safari only allows muted autoplay when the element's `muted` PROPERTY
  // is true at play time. React's declarative `muted` prop does not reliably
  // set that property in time, so the video would sit on its poster and never
  // start. Force the property (+ legacy webkit-playsinline) and kick playback
  // explicitly whenever a video announcement mounts or changes. Under reduced
  // motion we render tap-to-play controls instead (below), so we skip autoplay.
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.setAttribute('webkit-playsinline', 'true');
    if (reducedMotion) return;
    el.muted = true;
    const played = el.play();
    if (played && typeof played.catch === 'function') {
      // Still blocked (e.g. iOS Low Power Mode) — the poster remains, no throw.
      played.catch(() => {});
    }
  }, [shownId, reducedMotion, videoOk]);

  // Clear any pending re-present when the overlay unmounts (leaving the map),
  // so a forcedId set by the bell can't silently re-pop on a later map visit,
  // bypassing the guards.
  useEffect(() => () => { clearForced(); }, [clearForced]);

  if (!announcement) return null;

  // Overlay close = session-only. No server call: it returns next session and
  // stays in the bell. Drops the local auto target and any forced re-present.
  const close = () => {
    dismissForSession(announcement.id);
    setAutoId((cur) => (cur === announcement.id ? null : cur));
    if (forcedId) clearForced();
  };

  const onCta = () => {
    const link = announcement.ctaLink;
    // Acting on the CTA closes the overlay for THIS session and opens the
    // link. It no longer PERMANENTLY retires the announcement: it stays in
    // the notification bell and returns next session until the driver
    // explicitly dismisses it from the bell (founder direction: a tap on the
    // action button shouldn't make the announcement vanish forever).
    dismissForSession(announcement.id);
    setAutoId((cur) => (cur === announcement.id ? null : cur));
    if (forcedId) clearForced();
    if (!link) return;
    if (link.startsWith('/')) {
      onNavigate(link);
    } else {
      window.open(link, '_blank', 'noopener');
    }
  };

  return (
    <div className={styles.card} role="status">
      {/* Media slot — one slot, two renderers, identical geometry. A videoUrl
          plays Spotify-canvas style (muted/autoplay/loop/inline) with imageUrl
          as the poster. An announcement video is CONTENT, not decoration, so
          the <video> renders whenever there's a video: under reduced motion it
          shows tap-to-play controls (no autoplay/loop) instead of a dead poster.
          Only a video ERROR falls back to the image. The muted property +
          explicit play() (effect above) satisfy the iOS autoplay policy. */}
      {announcement.videoUrl && videoOk ? (
        <video
          ref={videoRef}
          className={styles.image}
          src={announcement.videoUrl}
          poster={announcement.imageUrl ?? undefined}
          autoPlay={!reducedMotion}
          loop={!reducedMotion}
          controls={reducedMotion}
          muted
          playsInline
          preload="auto"
          aria-hidden={reducedMotion ? undefined : true}
          onError={() => setVideoOk(false)}
        />
      ) : announcement.imageUrl && imageOk ? (
        <img
          className={styles.image}
          src={announcement.imageUrl}
          alt=""
          loading="lazy"
          onError={() => setImageOk(false)}
        />
      ) : null}
      <div className={styles.header}>
        <h2 className={styles.title}>{announcement.title}</h2>
        <button
          type="button"
          className={styles.dismiss}
          onClick={close}
          aria-label={t('close')}
        >
          <X size={16} />
        </button>
      </div>
      <p className={styles.body}>{announcement.body}</p>
      {announcement.ctaLabel && (
        <button type="button" className={styles.cta} onClick={onCta}>
          {announcement.ctaLabel}
        </button>
      )}
    </div>
  );
}
